import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { InfoIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import type { LastSyncHintContent } from '../utils/lastSyncHint';

const PANEL_WIDTH = 300;
const VIEWPORT_PADDING = 8;
const PANEL_GAP = 8;

function computePanelStyle(anchor: DOMRect): CSSProperties {
  const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2);
  let left = anchor.left;
  if (left + width > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - VIEWPORT_PADDING - width;
  }
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;

  const spaceBelow = window.innerHeight - anchor.bottom - PANEL_GAP - VIEWPORT_PADDING;
  const spaceAbove = anchor.top - PANEL_GAP - VIEWPORT_PADDING;
  const placeAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

  if (placeAbove) {
    return {
      position: 'fixed',
      left,
      width,
      top: anchor.top - PANEL_GAP,
      transform: 'translateY(-100%)',
      maxHeight: Math.min(360, Math.max(140, spaceAbove)),
      overflowY: 'auto',
    };
  }

  return {
    position: 'fixed',
    left,
    width,
    top: anchor.bottom + PANEL_GAP,
    maxHeight: Math.min(360, Math.max(140, spaceBelow)),
    overflowY: 'auto',
  };
}

interface LastSyncHintPopoverProps {
  content: LastSyncHintContent;
  className?: string;
}

export const LastSyncHintPopover: React.FC<LastSyncHintPopoverProps> = ({ content, className }) => {
  const { colors } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
  const panelId = React.useId();

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    setPanelStyle({
      ...computePanelStyle(el.getBoundingClientRect()),
      ...zStyle(Z_INDEX.portaledDropdown),
    });
  }, []);

  const showPanel = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleHide = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 150);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    updatePosition();
    const onLayout = () => updatePosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, updatePosition]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const panelContent =
    open && panelStyle ? (
      <div
        ref={panelRef}
        id={panelId}
        role="tooltip"
        style={panelStyle}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
      >
        <div
          className={cn(
            'rounded-xl border shadow-lg ring-1 ring-black/5 dark:ring-white/10 overflow-hidden',
            colors.surface,
            colors.border,
          )}
        >
          <div className={cn('px-3.5 py-3', colors.surfaceHover)}>
            <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
              Display timezone
            </p>
            <p className={cn('text-sm font-semibold mt-1 tabular-nums', colors.text)}>{content.primaryTime}</p>
            <p className={cn('text-xs mt-1', colors.textSecondary)}>
              {content.primaryZoneLabel}
              <span className={cn('mx-1.5', colors.textMuted)}>·</span>
              <span className={colors.textMuted}>{content.primaryOffset}</span>
            </p>
            <p className={cn('text-[10px] mt-1.5 leading-snug', colors.textMuted)}>{content.zoneCaption}</p>
          </div>

          {content.otherZones.length > 0 ? (
            <div className={cn('px-3.5 py-2.5 border-t', colors.border)}>
              <p className={cn('text-[10px] font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>
                Other timezones
              </p>
              <ul className="space-y-2">
                {content.otherZones.map((row) => (
                  <li key={row.id} className="flex items-baseline justify-between gap-3 min-w-0">
                    <div className="min-w-0">
                      <p className={cn('text-xs font-medium truncate', colors.text)}>{row.label}</p>
                      <p className={cn('text-[10px] mt-0.5', colors.textMuted)}>{row.offset}</p>
                    </div>
                    <span className={cn('text-xs tabular-nums whitespace-nowrap shrink-0', colors.textSecondary)}>
                      {row.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {content.mailboxRows && content.mailboxRows.length > 0 ? (
            <div className={cn('px-3.5 py-2.5 border-t', colors.border, colors.surfaceHover)}>
              <p className={cn('text-[10px] font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>
                Per mailbox
              </p>
              <ul className="space-y-2">
                {content.mailboxRows.map((row) => (
                  <li key={row.id} className="flex items-baseline justify-between gap-3 min-w-0">
                    <div className="min-w-0">
                      <p className={cn('text-xs font-medium truncate', colors.text)}>{row.label}</p>
                      {row.description ? (
                        <p className={cn('text-[10px] mt-0.5 truncate', colors.textMuted)}>{row.description}</p>
                      ) : null}
                    </div>
                    <span className={cn('text-xs tabular-nums whitespace-nowrap shrink-0', colors.textSecondary)}>
                      {row.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-md p-1 transition-colors',
          open
            ? cn(colors.primaryFg, 'bg-black/5 dark:bg-white/15')
            : cn('text-slate-500 hover:bg-black/5 dark:text-slate-400 dark:hover:bg-white/10'),
          className,
        )}
        aria-label="Timezone details"
        aria-describedby={open ? panelId : undefined}
        aria-expanded={open}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
        onFocus={showPanel}
        onBlur={(event) => {
          const next = event.relatedTarget as Node | null;
          if (next && panelRef.current?.contains(next)) return;
          setOpen(false);
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <InfoIcon className="w-3.5 h-3.5" />
      </button>
      {typeof document !== 'undefined' && panelContent ? createPortal(panelContent, document.body) : null}
    </>
  );
};
